import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import Avatar from '@strava/ui/Avatar';
import Button, { LinkButton } from '@strava/ui/Button';
import { Markup } from 'interweave';
import { useFeedEntityContext } from '../../../../state/FeedEntityContext';
import { buildTimeAgoTimestamp } from '../../../../utils';
import useFeedAnalytics from '../../../../hooks/useFeedAnalytics';
import { DELETE_COMMENT_START } from '../../../../state/feed-entity-actions';
import styles from './styles.scss';

const prefix = 'components.feed.social';

const CommentsThread = ({ onSeeMoreCommentsClick, entityId }) => {
  const { dispatch, entityState, analyticProps } = useFeedEntityContext();

  const { intersectionObserverRef } = useFeedAnalytics({
    ...analyticProps,
    element: 'comment_preview'
  });

  const comments = entityState.comments[entityId] || [];
  const numComments = comments.length;
  const totalComments = Math.min(numComments, 2);

  const deleteComment = (comment) => {
    dispatch({
      type: DELETE_COMMENT_START,
      payload: { comment, entityId, element: 'comments_thread' }
    });
  };

  const thread = () => {
    const entries = [];
    comments
      .slice(Math.max(numComments - totalComments, 0))
      .forEach((entry) => {
        const timeAgoTimestamp = buildTimeAgoTimestamp(entry.timestamp);
        entries.push(
          <li
            ref={intersectionObserverRef}
            key={entry.comment_id}
            className={styles.media}
          >
            <div className={styles.mediaLeft}>
              <Avatar
                name={entry.athlete.name}
                src={entry.athlete.avatar_url}
                type="athlete"
                size="xsmall"
                data-testid="comment_avatar"
                href={entry.athlete.url}
                badge={entry.athlete.member_type}
                onClick={() => {
                  entityState.trackAnalytics({
                    action: 'click',
                    element: 'profile_picture',
                    properties: { element_source: 'comments_thread' }
                  });
                }}
              />
            </div>
            <div className={`${styles.comment} ${styles.mediaBody}`}>
              <div className={styles.nameAndTime}>
                <div className={styles.athleteName}>
                  <a href={entry.athlete.url}>{entry.athlete.name}</a>
                </div>
                <div
                  className={styles.timestampAndCommentActions}
                  data-testid="comment-actions"
                >
                  <Markup noWrap={true} content={timeAgoTimestamp} />
                  {(entry.deletable || entry.reportable) && (
                    <div className={styles.commentActions}>
                      {entry.reportable && (
                        <LinkButton
                          variant="text"
                          data-testid="report-comment-btn"
                          className={styles.reportAndDeleteButtons}
                          href={`/comments/${entry.comment_id}/feedback`}
                        >
                          {I18n.t(`${prefix}.report`)}
                        </LinkButton>
                      )}
                      {entry.deletable && (
                        <Button
                          className={styles.reportAndDeleteButtons}
                          variant="text"
                          data-testid="delete-comment-btn"
                          onClick={() => deleteComment(entry)}
                          type="button"
                        >
                          {I18n.t(`${prefix}.delete`)}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <span className={styles.commentText}>
                <Markup noWrap={true} content={entry.comment} />
              </span>
            </div>
          </li>
        );
      });
    return entries;
  };

  return (
    <div className={styles.comments}>
      {numComments > 0 && <ol>{thread()}</ol>}
      {numComments > 2 && (
        <Button
          data-testid="see_all_comments_btn"
          className={styles.moreCommentsButton}
          variant="text"
          onClick={onSeeMoreCommentsClick}
          type="button"
        >
          {I18n.t(`${prefix}.see_all_comments`, {
            number: numComments
          })}
        </Button>
      )}
    </div>
  );
};

CommentsThread.propTypes = {
  onSeeMoreCommentsClick: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired
};

export default CommentsThread;
